import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import Breadcrumbs from "@/components/admin/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatApiError } from "@/lib/errors";

const COMMON_ICONS = [
  "file-text", "truck", "package", "clipboard-list", "receipt",
  "users", "map", "warehouse", "folder", "bar-chart-3",
];

/**
 * Copy-to-clipboard pill for an app's UUID. Compact form (`compact`) is the
 * table-row variant: first 8 chars + copy icon. Full form is the dialog
 * variant: full UUID in monospace + a clearly-labelled Copy button.
 *
 * The same UUID returned by `GET /admin/apps` is what the SSO launch token
 * uses as its `aud` claim — there is no separate registry-id / audience-id.
 * That's why the helper text in the dialog tells integrators to drop this
 * straight into their app's SSO config.
 */
function CopyId({ id, compact = false }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(id);
    } catch {
      // Fallback for browsers blocking the async API (e.g. http preview).
      const ta = document.createElement("textarea");
      ta.value = id;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("App ID copied");
    setTimeout(() => setCopied(false), 1500);
  };

  if (compact) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleCopy}
              data-testid={`app-copy-id-${id}`}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-[11px] text-slate-600 transition hover:border-slate-300 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-cargodash-primary-500/40"
            >
              <span>{id.slice(0, 8)}</span>
              {copied ? (
                <Check className="h-3 w-3 text-emerald-600" />
              ) : (
                <Copy className="h-3 w-3 text-slate-400" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="font-mono text-[11px]">
            <div>Click to copy full UUID</div>
            <div className="opacity-70">{id}</div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="app-id-full">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">App ID (UUID)</Label>
          <code className="mt-1 block truncate font-mono text-xs text-slate-800" data-testid="app-id-full-value">{id}</code>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleCopy}
          data-testid="app-id-full-copy"
          className="shrink-0 gap-1.5"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">
        Use this ID in your app's SSO_PROTOCOL config as the <code className="font-mono">app_id</code> claim recipient.
      </p>
    </div>
  );
}

function AppDialog({ open, onOpenChange, app, onSaved }) {
  const isEdit = !!app;
  const [form, setForm] = useState({
    name: "",
    url: "",
    description: "",
    icon: "file-text",
    embeddable: false,
    sso_enabled: true,
    status: "active",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (app) {
      setForm({ ...app });
    } else {
      setForm({
        name: "",
        url: "https://",
        description: "",
        icon: "file-text",
        embeddable: false,
        sso_enabled: true,
        status: "active",
      });
    }
  }, [open, app]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEdit) {
        const { data } = await api.patch(`/admin/apps/${app.id}`, form);
        toast.success("App updated");
        onSaved(data);
      } else {
        const { data } = await api.post("/admin/apps", form);
        toast.success("App created");
        onSaved(data);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="app-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit app" : "Register app"}</DialogTitle>
          <DialogDescription>
            Apps appear in the launcher and can accept SSO tokens.
          </DialogDescription>
        </DialogHeader>
        {isEdit && app?.id && (
          <div className="mb-2">
            <CopyId id={app.id} />
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="app-form-name"
            />
          </div>
          <div className="space-y-2">
            <Label>URL</Label>
            <Input
              required
              type="url"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://…"
              data-testid="app-form-url"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="app-form-description"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Icon</Label>
              <Select
                value={form.icon}
                onValueChange={(v) => setForm({ ...form, icon: v })}
              >
                <SelectTrigger data-testid="app-form-icon">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_ICONS.map((i) => (
                    <SelectItem key={i} value={i}>
                      {i}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger data-testid="app-form-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
            <div>
              <div className="text-sm font-medium">Embeddable</div>
              <div className="text-xs text-slate-500">Allow iframe hosting in the hub.</div>
            </div>
            <Switch
              checked={form.embeddable}
              onCheckedChange={(v) => setForm({ ...form, embeddable: v })}
              data-testid="app-form-embeddable"
            />
          </div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
            <div>
              <div className="text-sm font-medium">SSO enabled</div>
              <div className="text-xs text-slate-500">Accept CargoDash SSO tokens.</div>
            </div>
            <Switch
              checked={form.sso_enabled}
              onCheckedChange={(v) => setForm({ ...form, sso_enabled: v })}
              data-testid="app-form-sso"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="app-form-submit"
              className="bg-cargodash-primary-600 hover:bg-cargodash-primary-700"
            >
              {submitting ? "Saving…" : isEdit ? "Save" : "Register app"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminApps() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/apps");
      setApps(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const doDelete = async () => {
    const a = confirmDelete;
    setConfirmDelete(null);
    if (!a) return;
    try {
      await api.delete(`/admin/apps/${a.id}`);
      toast.success(`${a.name} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed");
    }
  };

  return (
    <div data-testid="admin-apps" className="mx-auto w-full max-w-6xl">
      <Breadcrumbs />
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
            Apps
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {apps.length} app{apps.length === 1 ? "" : "s"} registered.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          data-testid="apps-create-btn"
          className="gap-2 bg-cargodash-primary-600 hover:bg-cargodash-primary-700"
        >
          <Plus className="h-4 w-4" />
          New app
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">Name</TableHead>
              <TableHead>App ID</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Icon</TableHead>
              <TableHead>Embeddable</TableHead>
              <TableHead>SSO</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="pr-4 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-sm text-slate-500">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && apps.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-sm text-slate-500">
                  No apps registered yet.
                </TableCell>
              </TableRow>
            )}
            {apps.map((a) => (
              <TableRow key={a.id} data-testid={`app-row-${a.id}`}>
                <TableCell className="pl-4">
                  <div className="font-medium text-slate-900">{a.name}</div>
                  <div className="text-xs text-slate-500">{a.description}</div>
                </TableCell>
                <TableCell>
                  <CopyId id={a.id} compact />
                </TableCell>
                <TableCell>
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-cargodash-primary-700 hover:underline"
                  >
                    {a.url.replace(/^https?:\/\//, "")}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </TableCell>
                <TableCell>
                  <code className="font-mono text-xs text-slate-600">{a.icon || "—"}</code>
                </TableCell>
                <TableCell className="text-sm">{a.embeddable ? "Yes" : "No"}</TableCell>
                <TableCell className="text-sm">{a.sso_enabled ? "Yes" : "No"}</TableCell>
                <TableCell>
                  {a.status === "active" ? (
                    <Badge className="bg-cargodash-primary-50 text-cargodash-primary-700 hover:bg-cargodash-primary-50">
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Inactive</Badge>
                  )}
                </TableCell>
                <TableCell className="pr-4 text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditing(a);
                        setFormOpen(true);
                      }}
                      data-testid={`app-edit-${a.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setConfirmDelete(a)}
                      data-testid={`app-delete-${a.id}`}
                      className="text-cargodash-alert-600 hover:bg-cargodash-alert-600/10 hover:text-cargodash-alert-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AppDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        app={editing}
        onSaved={load}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent data-testid="app-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete app "{confirmDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the app from every user and role that had access.
              SSO tokens already issued remain valid until they expire.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-cargodash-alert-600 hover:bg-cargodash-alert-600/90"
            >
              Delete app
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
